/**
 * @file ArchivePurchaseOrdersFlow.js
 * @version 1.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-05
 * @modified 2026-11-03
 * @description Archiving purchase orders on their own — the sequence Search
 *              Purchase Orders has always run, moved here unchanged so the
 *              transport planning boards run the very same one for a row's PO:
 *              ArchivePOsFlow (checklist → cancellation emails for sent POs →
 *              archive), then POST /api/purchase-orders/archive, which archives
 *              all or nothing and audits each PO (poArchiveService).
 *
 *              `source` is a board's label (utils/archiveSource.js on the
 *              server); without it the route records "Search Purchase Orders".
 *
 *              1.1.0 (2026-10-13): a self-billed PO takes its own PO-H with it
 *              (the server archives it, `archived_with` in the answer). One its
 *              haulier already has is offered its cancellation email straight
 *              after, before the flow reports done.
 *
 *              1.2.0 (2026-10-26, Cam): POs made in Xero can be archived too
 *              (the Xero PO sync nightly / off) — the server DELETES them in
 *              Xero as it archives them, one at a time, at most ten a request.
 *              The checklist says so; the answer may archive some and not
 *              others (`failed`, `deferred`), which archiveResultMessage puts
 *              in words.
 *
 *              1.3.0 (2026-11-03, Cam — SO-D10696 left behind with nothing
 *              bought for it): before the checklist, POST /archive-check names
 *              the customer orders these POs are everything bought for, and
 *              ArchiveOrdersLeftDialog asks for each — archive the order too,
 *              or keep it (no default). An order going too is archived through
 *              its own archive (PATCH /customer-orders/:id/archive) with its
 *              ticked POs, its other live POs (a PO-H) added to the checklist
 *              so their supplier or haulier can be told; the rest go through
 *              POST /archive as before. Rules: utils/archiveOrdersLeft.js.
 */

import React, { useEffect, useRef, useState } from 'react';
import ArchivePOsFlow from './ArchivePOsFlow';
import ArchiveOrdersLeftDialog from './ArchiveOrdersLeftDialog';
import SendToSupplierFlow from './SendToSupplierFlow';
import { patchOrderArchive } from './ArchiveOrderFlow';
import { API_URL } from '../config';
import {
  checklistPos, ordersGoingText, ordersOutcomeText, planArchive
} from '../utils/archiveOrdersLeft';

/** Archive POs by local id. Throws with the server's reason on a refusal. */
export async function archivePurchaseOrders(ids, { source = null } = {}) {
  const res = await fetch(`${API_URL}/api/purchase-orders/archive`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, ...(source ? { source } : {}) })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not archive');
  return data;
}

/** The customer orders these POs are everything bought for (read only). */
export async function fetchOrdersLeftWithoutPo(ids) {
  const res = await fetch(`${API_URL}/api/purchase-orders/archive-check`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not check the customer orders');
  return Array.isArray(data.orders) ? data.orders : [];
}

/** Made in Xero: a real Xero id (not LOCAL-/TEMP-), never a PO-H. */
export const isXeroMadeRow = (po) => {
  const id = String(po?.xero_purchase_order_id || '');
  return !!id && po?.po_type !== 'haulage' && !/^(TEMP-|LOCAL-)/i.test(id);
};

/** What an archive answer did, in words: archived (and how in Xero), refused, still to do. */
export function archiveResultMessage(data) {
  const archived = (data?.archived || []).filter(a => !a.archived_with);
  const withIt = (data?.archived || []).filter(a => a.archived_with);
  const parts = [];
  if (archived.length) {
    const xero = archived.filter(a => a.xero);
    const deleted = xero.filter(a => a.xero === 'deleted').map(a => a.purchase_order_number);
    const already = xero.filter(a => a.xero === 'already deleted').map(a => a.purchase_order_number);
    parts.push(`Archived ${archived.map(p => p.purchase_order_number).join(', ')}. They are off their customer orders and the planning boards — turn off Active only to see them.`);
    if (deleted.length) parts.push(`Deleted in Xero too: ${deleted.join(', ')}.`);
    if (already.length) parts.push(`Already deleted in Xero: ${already.join(', ')} — archived here to match.`);
  }
  if (withIt.length) parts.push(`Archived with them: ${withIt.map(p => `${p.purchase_order_number} (with ${p.archived_with})`).join(', ')}.`);
  const failed = data?.failed || [];
  if (failed.length) parts.push(`Not archived: ${failed.map(f => `${f.purchase_order_number} — ${f.error}`).join('; ')}`);
  if (data?.deferred_note) parts.push(data.deferred_note);
  const orders = ordersOutcomeText(data);
  if (orders) parts.push(orders);
  return parts.join(' ') || 'Nothing was archived.';
}

/**
 * @param {Array}    pos      ArchivePOsFlow rows: { id, purchase_order_number,
 *                            xero_purchase_order_id, po_type, status,
 *                            supplier_name, sent_to_contact, blocked }
 * @param {object}   user     For "send as" in the cancellation emails.
 * @param {string}   source   Optional board label.
 * @param {function} onDone   (data) — archived; data.archived lists them.
 * @param {function} onClose  Cancelled; nothing changed.
 */
/** The PO-Hs archived with a self-billed PO that their haulier already holds. */
export function haulageToTell(data) {
  return (data?.archived || []).filter(a => a.archived_with && a.sent_to_contact);
}

/** The checklist's note when any PO was made in Xero. */
function xeroNote(pos) {
  const xero = (pos || []).filter(p => isXeroMadeRow(p) && !p.blocked);
  if (!xero.length) return null;
  const names = xero.map(p => p.purchase_order_number).join(', ');
  return `${xero.length === 1 ? `${names} was` : `${names} were`} made in Xero: archiving deletes ${xero.length === 1 ? 'it' : 'them'} in Xero too, `
    + 'and Xero cannot bring a deleted purchase order back, so it cannot be restored here afterwards. '
    + 'One already deleted in Xero is just archived here. If Xero refuses (billed there, or it cannot be reached), that PO is not archived.';
}

function ArchivePurchaseOrdersFlow({ pos, user, source = null, onDone, onClose }) {
  const [followUp, setFollowUp] = useState(null); // { data, tell }
  // The orders these POs are everything bought for: null while asking.
  const [check, setCheck] = useState(null);       // { orders, error }
  const [choices, setChoices] = useState({});     // { [order id]: 'archive' | 'keep' }
  const [asked, setAsked] = useState(false);
  const started = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const ids = (pos || []).filter(p => !p.blocked).map(p => p.id);
    (async () => {
      try {
        const orders = ids.length ? await fetchOrdersLeftWithoutPo(ids) : [];
        if (alive.current) setCheck({ orders, error: null });
      } catch (error) {
        // Never in the way of archiving: say it could not be checked.
        if (alive.current) setCheck({ orders: [], error: error.message });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (followUp) {
    return (
      <SendToSupplierFlow
        linkedPos={followUp.tell.map(h => ({ ...h, cancellation: { reason: `purchase order ${h.archived_with} cancelled` } }))}
        orderNumber={null}
        user={user}
        onClose={() => onDone(followUp.data)}
      />
    );
  }

  if (!check) return null;
  const orders = check.orders || [];
  if (orders.length && !asked) {
    return (
      <ArchiveOrdersLeftDialog
        orders={orders}
        choices={choices}
        onChoose={(id, value) => setChoices(prev => ({ ...prev, [id]: value }))}
        onContinue={() => setAsked(true)}
        onClose={onClose}
      />
    );
  }

  const listed = checklistPos(pos, orders, choices);
  const going = ordersGoingText(orders, choices);
  const notes = [
    xeroNote(listed),
    check.error ? `Could not check whether these are everything bought for a customer order (${check.error}) — an order left with nothing bought stays live; archive it on Search Customer Orders.` : null
  ].filter(Boolean).join(' ') || null;

  return (
    <ArchivePOsFlow
      title={pos.length === 1 ? `Archive ${pos[0].purchase_order_number}` : `Archive ${pos.length} purchase orders`}
      intro={`Archived purchase orders come off their customer orders and the planning boards, as deleting did, but are kept with their history and can be restored. A supplier who already has one can be sent a cancellation first.${going ? ` ${going}` : ''}`}
      pos={listed}
      note={notes}
      reason={null}
      confirmLabel={(n) => `Archive ${n} PO${n === 1 ? '' : 's'}`}
      user={user}
      onArchive={async (ids) => {
        const plan = planArchive(ids, orders, choices);
        const ticked = new Set(ids.map(Number));
        const ordersArchived = [];
        const withOrders = [];
        for (const call of plan.orderCalls) {
          try {
            const d = await patchOrderArchive(call.order.id, true, call.poIds, { source });
            ordersArchived.push(call.order.order_number);
            withOrders.push(...(d.archived_pos || []).map(p => ({ ...p, with_order: call.order.order_number })));
          } catch (e) {
            throw new Error(ordersArchived.length ? `${ordersArchived.join(', ')} archived; then ${call.order.order_number}: ${e.message}` : e.message);
          }
        }
        let data = { success: true, archived: [], failed: [] };
        if (plan.rest.length) {
          try {
            data = await archivePurchaseOrders(plan.rest, { source });
          } catch (e) {
            throw new Error(ordersArchived.length ? `${ordersArchived.join(', ')} archived with ${ordersArchived.length === 1 ? 'its' : 'their'} POs; the others were not: ${e.message}` : e.message);
          }
        }
        const merged = {
          ...data,
          archived: [...withOrders, ...(data.archived || [])],
          orders_archived: ordersArchived,
          orders_kept: plan.kept
            .filter(o => (o.po_ids || []).some(id => ticked.has(Number(id))))
            .map(o => ({ order_number: o.order_number }))
        };
        const tell = haulageToTell(merged);
        if (tell.length) setFollowUp({ data: merged, tell }); else onDone(merged);
      }}
      onClose={onClose}
    />
  );
}

export default ArchivePurchaseOrdersFlow;
